import { GetIssueRelationshipsResponseDto } from '@issuemanager/api-client';
import { ConfirmationModal } from '@issuemanager/apps/client/components';
import {
  useLocalizedRelationshipTypes,
  useRemoveRelationship,
} from '@issuemanager/client/features/issues';
import { useSnackbar } from 'notistack';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';

type RemoveRelationshipModalProps = {
  isOpen: boolean;
  relationship: GetIssueRelationshipsResponseDto | null;
  code: string;
  onClose: () => void;
};

export default function RemoveRelationshipModal({
  isOpen,
  relationship,
  code,
  onClose,
}: RemoveRelationshipModalProps): JSX.Element {
  const { t } = useTranslation(['issue']);
  const { enqueueSnackbar } = useSnackbar();
  const { getRelationshipTypeLabel } = useLocalizedRelationshipTypes();
  const removeRelationship = useRemoveRelationship(code);

  const handleAccept = async (): Promise<void> => {
    if (!relationship) return;

    await removeRelationship.mutateAsync(
      {
        relationshipId: relationship.relatedObject.id,
        relatedObjectType: relationship.relatedObject.type ?? '',
        relatedObjectService: relationship.relatedObject.service,
        relatedObjectName: relationship.relatedObject.name ?? '',
      },
      {
        onSuccess: () => {
          enqueueSnackbar(
            t('relationships.removeSuccess', {
              type: getRelationshipTypeLabel(relationship.relatedObject.type),
            }),
            { variant: 'success' },
          );
          onClose();
        },
        onError: () => {
          enqueueSnackbar(
            t('relationships.removeError', {
              type: getRelationshipTypeLabel(relationship.relatedObject.type),
            }),
            { variant: 'error' },
          );
        },
      },
    );
  };

  return (
    <ConfirmationModal
      onAccept={handleAccept}
      onCancel={onClose}
      isOpen={isOpen}
      title={t('issue:relationships.confirmRemove.title')}
      description={t('issue:relationships.confirmRemove.description', {
        code: relationship?.relatedObject.id,
        type: getRelationshipTypeLabel(relationship?.relatedObject.type),
        title: relationship?.relatedObject.name,
      })}
      swapButtons
      acceptButtonProps={{
        text: removeRelationship.isPending
          ? t('issue:relationships.confirmRemove.accepting')
          : t('issue:relationships.confirmRemove.accept'),
        disabled: removeRelationship.isPending,
        loading: removeRelationship.isPending,
      }}
      cancelButtonProps={{
        text: t('issue:relationships.confirmRemove.cancel'),
        disabled: removeRelationship.isPending,
      }}
    />
  );
}
